import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
// Routing
import { Router } from '@angular/router';
// Services
import { DroneOperatorService } from 'src/app/services/drone-operator.service';
import { FilesService } from 'src/app/services/files.service';
import { HttpResponsesService } from 'src/app/services/httpResponses.service';
// Models
import { Vehicle, User, Pilot, FlightSession, Insurance, DroneOperator } from '@mocca-technologies/model';
// Utils
import { droneIcon, droneMoccaIcon } from 'src/assets/icons';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-vehicle-list',
  templateUrl: './vehicle-list.component.html',
  styleUrls: ['./vehicle-list.component.scss'],
})
export class VehicleListComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Vehicles list provided by parent (loaded once).
   * - ADMIN/DEVEL: all org vehicles
   * - AUTH/Restricted: already-associated vehicles (sessions -> vehicles)
   */
  @Input() vehicles: Vehicle[] = [];

  @Input() selectedPilot: Pilot;
  @Input() selectedVehicle: Vehicle;
  @Input() showList: boolean = false;
  @Input() operator: DroneOperator;

  @Output() selectVehicleGlobally = new EventEmitter<Vehicle>();

  filteredVehicleList: Vehicle[] = [];
  sessionsList: FlightSession[] = [];
  displayedVehicleIndexes: number[] = [];

  droneMoccaIcon = droneMoccaIcon;
  droneIcon = droneIcon;

  user: User;

  @ViewChild('vehiclesList') vehiclesList: ElementRef;

  private destroy$ = new Subject<void>();

  // -------------------- Getters --------------------

  getNumVehicles(): number {
    return this.filteredVehicleList.length;
  }

  get isAdminDevel(): boolean {
    return this.user?.profile === 'admin' || this.user?.profile === 'devel';
  }

  get isSubOperatorView(): boolean {
    return !!this.operator;
  }

  isSelected(index: number): boolean {
    return this.displayedVehicleIndexes.includes(index);
  }

  constructor(
    private droneOperatorService: DroneOperatorService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private filesService: FilesService,
    private httpResponseService: HttpResponsesService,
    private translateService: TranslateService,
  ) {}

  // -------------------- Lifecycle --------------------

  ngOnInit(): void {
    this.user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;
    this.applyVehiclesInput();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vehicles']) {
      this.applyVehiclesInput();
    }

    // IMPORTANT:
    // This filter is ONLY for admin/devel (available vehicles = vehicles NOT already associated to selectedPilot)
    if (changes['selectedPilot']) {
      if (this.isAdminDevel && this.selectedPilot && !this.showList && !this.isSubOperatorView) {
        this.filterVehicleListAccordingToSessions_NoLoader();
      } else {
        // AUTH/Restricted (or other contexts) -> just show what parent passed
        this.applyVehiclesInput();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.selectedVehicle?.avatarUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.selectedVehicle.avatarUrl);
    }
  }

  // -------------------- Data handling --------------------

  private applyVehiclesInput(): void {
    const list = Array.isArray(this.vehicles) ? this.vehicles : [];

    // AUTH/Restricted: vehicles already represent "associated vehicles" -> show all
    if (!this.isAdminDevel) {
      this.filteredVehicleList = [...list];
      this.cdr.detectChanges();
      return;
    }

    // ADMIN/DEVEL:
    // - If selectedPilot exists and we are in main view, we will filter out associated ones
    // - Otherwise show all
    this.filteredVehicleList = [...list];

    if (this.selectedPilot && !this.showList && !this.isSubOperatorView) {
      this.filterVehicleListAccordingToSessions_NoLoader();
      return;
    }

    this.cdr.detectChanges();
  }

  /**
   * ADMIN ONLY:
   * Filters vehicles list so that it shows vehicles that are NOT associated to selectedPilot
   * (this is the "available vehicles to drag into sessions" behavior).
   */
  private filterVehicleListAccordingToSessions_NoLoader(): void {
    if (!this.isAdminDevel) return;
    if (!this.selectedPilot?.uniqueId) return;

    this.filteredVehicleList = [];
    this.sessionsList = [];

    this.droneOperatorService
      .getUserSessions(this.selectedPilot.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const sessions = (res?.body?.responseObject || []) as FlightSession[];

          if (sessions && sessions.length > 0) {
            this.sessionsList = sessions;

            for (const vehicle of this.vehicles || []) {
              if (!vehicle?.uniqueId) continue;

              // if session exists -> vehicle is already associated -> do NOT show in available list
              const session = this.sessionsList.find((s) => s?.vehicleUuid === vehicle.uniqueId);
              if (!session) this.filteredVehicleList.push(vehicle);
            }
          } else {
            this.filteredVehicleList = [...(this.vehicles || [])];
          }

          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          this.httpResponseService.showServerError(error?.status, error?.error);
          this.filteredVehicleList = [...(this.vehicles || [])];
          this.cdr.detectChanges();
        },
      });
  }

  // -------------------- UI helpers --------------------

  noInsuranceInfo(insurance: Insurance): boolean {
    return (
      !insurance ||
      (!insurance?.insuranceNumber &&
        !insurance?.expirationDate &&
        !insurance?.insuranceIssuer &&
        !insurance?.country)
    );
  }

  noCompleteInsuranceInfo(insurance: Insurance): boolean {
    return (
      !this.noInsuranceInfo(insurance) &&
      (!insurance ||
        !insurance?.insuranceNumber ||
        !insurance?.expirationDate ||
        !insurance?.insuranceIssuer ||
        !insurance?.country)
    );
  }

  toggleVehicleItemState(index: number): void {
    if (this.displayedVehicleIndexes.includes(index)) {
      this.displayedVehicleIndexes = this.displayedVehicleIndexes.filter((i) => i !== index);
    } else {
      this.displayedVehicleIndexes.push(index);

      setTimeout(() => {
        const vehicle = this.filteredVehicleList[index];
        const element = document.getElementById('vehicle-' + vehicle?.uniqueId);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }

    this.cdr.detectChanges();
  }

  // -------------------- Selection --------------------

  selectVehicle(vehicle: Vehicle): void {
    if (!vehicle) {
      this.selectedVehicle = null;
      this.selectVehicleGlobally.emit(null);
      this.cdr.detectChanges();
      return;
    }

    if (vehicle?.uniqueId === this.selectedVehicle?.uniqueId) {
      this.selectedVehicle = null;
      this.selectVehicleGlobally.emit(null);
      this.cdr.detectChanges();
      return;
    }

    this.filesService
      .getDroneImage(vehicle.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          if (!blob || blob.size === 0) {
            vehicle.avatarUrl = null;
          } else {
            if (vehicle.avatarUrl?.startsWith('blob:')) URL.revokeObjectURL(vehicle.avatarUrl);
            vehicle.avatarUrl = URL.createObjectURL(blob);
          }

          this.selectedVehicle = vehicle;
          this.selectVehicleGlobally.emit(vehicle);
          this.cdr.detectChanges();
        },
        error: async (error: any) => {
          if (error?.status !== 422) {
            let body = error?.error;
            if (body instanceof Blob) {
              body = await body.text();
              try {
                body = JSON.parse(body);
              } catch {}
            }
            this.httpResponseService.showServerError(error?.status, body);
          } else {
            vehicle.avatarUrl = null;
          }

          this.selectedVehicle = vehicle;
          this.selectVehicleGlobally.emit(vehicle);
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Handles click anywhere on a vehicle row.
   * Admin/devel: selects the vehicle. Non-admin: toggles accordion.
   */
  onVehicleRowClick(vehicle: Vehicle, index: number): void {
    if (this.isAdminDevel && !this.showList && !this.selectedPilot) {
      this.selectVehicle(vehicle);
    } else if (!this.isAdminDevel && !this.showList) {
      this.toggleVehicleItemState(index);
    }
  }

  goToVehicleForm(): void {
    this.router.navigate(['/vehicle-form'], {
      state: { operatorId: this.operator?.uniqueId },
    });
  }
}
